/**
 * Utility functions for locale-aware date and time formatting.
 * Uses formatting registry for locale (DIP).
 */

import { getFormattingRegistry } from 'utils/formatting';

function getLocale(): string {
  return getFormattingRegistry().locale.getLocale();
}

/**
 * Formats a date/time in a locale-aware manner
 * US: 12-hour format (e.g., "1/15/2024, 2:30 PM")
 * Europe: 24-hour format (e.g., "15/1/2024, 14:30")
 * @param date - The date to format
 * @param options - Optional formatting options
 * @returns Formatted date string
 */
export const formatDateTime = (
  date: Date | string,
  options: Intl.DateTimeFormatOptions = {},
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (Number.isNaN(dateObj.getTime())) {
    return 'Invalid date';
  }

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...options,
  };

  const locale = getLocale();

  try {
    return new Intl.DateTimeFormat(locale, defaultOptions).format(dateObj);
  } catch {
    return new Intl.DateTimeFormat('en-US', defaultOptions).format(dateObj);
  }
};

/**
 * Formats just the time in a locale-aware manner
 * US: 12-hour format (e.g., "2:30 PM")
 * Europe: 24-hour format (e.g., "14:30")
 * @param date - The date to extract time from
 * @returns Formatted time string
 */
export const formatTime = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (Number.isNaN(dateObj.getTime())) {
    return 'Invalid date';
  }

  const locale = getLocale();
  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
  };

  try {
    return new Intl.DateTimeFormat(locale, options).format(dateObj);
  } catch {
    return new Intl.DateTimeFormat('en-US', options).format(dateObj);
  }
};

/**
 * Formats just the date in a locale-aware manner
 * @param date - The date to format
 * @returns Formatted date string
 */
export const formatDate = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (Number.isNaN(dateObj.getTime())) {
    return 'Invalid date';
  }

  const locale = getLocale();
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  };

  try {
    return new Intl.DateTimeFormat(locale, options).format(dateObj);
  } catch {
    return new Intl.DateTimeFormat('en-US', options).format(dateObj);
  }
};

/**
 * Formats a relative time (e.g., "2 minutes ago", "1 hour ago")
 * @param date - The date to compare against now
 * @returns Relative time string
 */
export const formatRelativeTime = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  }
  if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  }
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  }
  if (diffInSeconds < 2592000) {
    // 30 days
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  }
  // For older dates, show the actual date
  return formatDateTime(dateObj);
};
